import { createRouter, createWebHistory, type RouteLocationRaw } from 'vue-router'
import { useAuthStore } from '@/stores/auth'

const router = createRouter({
  history: createWebHistory(import.meta.env.BASE_URL),
  routes: [
    {
      path: '/',
      name: 'home',
      component: () => import('@/views/HomeView.vue'),
      meta: { title: 'Gettourly: listing photos in, property video out', marketing: true },
    },
    {
      path: '/library',
      name: 'library',
      component: () => import('@/views/LibraryView.vue'),
      meta: { title: 'My videos · Gettourly', requiresAuth: true, ownBar: true },
    },
    {
      path: '/pricing',
      name: 'pricing',
      component: () => import('@/views/PricingView.vue'),
      meta: { title: 'Pricing · Gettourly', marketing: true },
    },
    {
      path: '/terms',
      name: 'terms',
      component: () => import('@/views/TermsView.vue'),
      meta: { title: 'Terms · Gettourly', marketing: true },
    },
    {
      path: '/privacy',
      name: 'privacy',
      component: () => import('@/views/PrivacyView.vue'),
      meta: { title: 'Privacy · Gettourly', marketing: true },
    },
    {
      path: '/refund',
      name: 'refund',
      component: () => import('@/views/RefundView.vue'),
      meta: { title: 'Refunds · Gettourly', marketing: true },
    },
    {
      path: '/cookies',
      name: 'cookies',
      component: () => import('@/views/CookiesView.vue'),
      meta: { title: 'Cookies · Gettourly', marketing: true },
    },
    {
      path: '/login',
      name: 'login',
      component: () => import('@/views/LoginView.vue'),
      meta: { title: 'Log in · Gettourly', redirectIfAuth: true, marketing: true }
    },
    {
      path: '/register',
      name: 'register',
      component: () => import('@/views/RegisterView.vue'),
      meta: { title: 'Create account · Gettourly', redirectIfAuth: true, marketing: true }
    },
    {
      path: '/tours/:id',
      name: 'tour-detail',
      component: () => import('@/views/CreateVideoView.vue'),
      meta: { requiresAuth: true, fullBleed: true, ownBar: true },
      props: route => ({ tourId: Number(route.params.id) }),
    },
    {
      path: '/verify-email',
      name: 'verify-email',
      component: () => import('@/views/VerifyEmailView.vue'),
      meta: { title: 'Verify your email · Gettourly', marketing: true },
    },
    {
      path: '/oauth/callback',
      name: 'oauth-callback',
      component: () => import('@/views/OAuthCallbackView.vue'),
      meta: { title: 'Signing in · Gettourly', marketing: true },
    },
    {
      path: '/forgot-password',
      name: 'forgot-password',
      component: () => import('@/views/ForgotPasswordView.vue'),
      meta: { title: 'Reset your password · Gettourly', redirectIfAuth: true, marketing: true }
    },
    {
      path: '/reset-password',
      name: 'reset-password',
      component: () => import('@/views/ResetPasswordView.vue'),
      meta: { title: 'Set a new password · Gettourly', marketing: true },
    },
    // Redirects for old routes. Bare /tours joins them: the workspace is only
    // reachable with a tour id now, and the Library is where you pick one.
    { path: '/tours', redirect: '/library' },
    { path: '/dashboard', redirect: '/library' },
    { path: '/workspace', redirect: '/library' },
    { path: '/new', redirect: '/library' },
    {
      path: '/:pathMatch(.*)*',
      name: 'not-found',
      component: () => import('@/views/NotFoundView.vue'),
      meta: { title: 'Page not found · Gettourly', marketing: true },
    },
  ]
})

// Navigation guard
router.beforeEach(async (to, _from, next) => {
  const authStore = useAuthStore()

  // Load user from storage if not already loaded
  if (!authStore.isAuthenticated && localStorage.getItem('accessToken')) {
    await authStore.loadUserFromStorage()
  }

  const requiresAuth = to.matched.some(record => record.meta.requiresAuth)
  const redirectIfAuth = to.matched.some(record => record.meta.redirectIfAuth)

  if (requiresAuth && !authStore.isAuthenticated) {
    // Carry the deep link so the login page can bring the user back to it.
    next({ name: 'login', query: { redirect: to.fullPath } })
  } else if (redirectIfAuth && authStore.isAuthenticated) {
    next(postAuthDestination(to.query.redirect))
  } else if (to.name === 'home' && authStore.isAuthenticated) {
    // The marketing hero is for visitors; a signed-in user wants their library.
    next({ name: 'library' })
  } else {
    next()
  }
})

// The tab's title: the route's, except the editor, which names the video once it loads (CreateVideoView).
router.afterEach((to) => {
  if (typeof to.meta.title === 'string') document.title = to.meta.title
})

/**
 * Where to send a user once signed in: the `?redirect=` they arrived with,
 * else the library. Only a path that resolves to one of our `requiresAuth`
 * routes is honoured — that rejects absolute URLs, `//host`, `/login`,
 * `/oauth/callback` and anything unknown without a hand-kept list.
 */
export function postAuthDestination(raw: unknown): RouteLocationRaw {
  if (typeof raw === 'string' && raw.startsWith('/') && !raw.startsWith('//')) {
    const resolved = router.resolve(raw)
    if (resolved.matched.some((r) => r.meta.requiresAuth)) return raw
  }
  return { name: 'library' }
}

// The Google round-trip leaves the SPA, and the backend callback echoes only
// the tokens, so the redirect rides in sessionStorage (per tab, like the
// OAuth flow itself). Read once, then cleared.
const REDIRECT_STORAGE_KEY = 'gettourly.postAuthRedirect'

export function stashRedirect(raw: unknown) {
  if (typeof raw === 'string' && raw) sessionStorage.setItem(REDIRECT_STORAGE_KEY, raw)
  else sessionStorage.removeItem(REDIRECT_STORAGE_KEY)
}

export function takeStashedRedirect(): string | null {
  const value = sessionStorage.getItem(REDIRECT_STORAGE_KEY)
  sessionStorage.removeItem(REDIRECT_STORAGE_KEY)
  return value
}

export default router
